"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { safeGet, safeRemove, safeSet } from "@/components/onboarding/safe-storage";
import {
  LIVE_BROADCAST_CHANNELS,
  LIVE_BROADCAST_CLIENT_POLL_INTERVAL_MS,
  LIVE_WIDGET_AUTO_COLLAPSE_MS,
  LIVE_WIDGET_SHOW_DELAY_MS,
  getLiveBroadcastPriority,
  liveBroadcastDismissedKey,
} from "@/lib/live-broadcast-config";
import type {
  LiveBroadcastApiResponse,
  LiveBroadcastStream,
  LiveWidgetStage,
} from "@/lib/live-broadcast-types";
import { phCapture } from "@/utils/analytics";

export interface LiveBroadcastContextValue {
  isLive: boolean;
  streams: LiveBroadcastStream[];
  activeStream: LiveBroadcastStream | null;
  stage: LiveWidgetStage;
  expand: () => void;
  dismiss: () => void;
  clickCta: (watchUrl: string, stream?: LiveBroadcastStream, surface?: "chat") => void;
}

const LiveBroadcastContext = createContext<LiveBroadcastContextValue | null>(null);

export function useLiveBroadcasts(): LiveBroadcastContextValue {
  const ctx = useContext(LiveBroadcastContext);
  if (!ctx) {
    throw new Error("useLiveBroadcasts must be used within LiveBroadcastProvider");
  }
  return ctx;
}

function sortStreamsByPriority(streams: LiveBroadcastStream[]): LiveBroadcastStream[] {
  return [...streams].sort((a, b) => getLiveBroadcastPriority(a) - getLiveBroadcastPriority(b));
}

function captureLiveBroadcastEvent(
  action: "shown" | "expanded" | "dismissed" | "clicked",
  stream: LiveBroadcastStream,
  extra: Record<string, unknown> = {},
) {
  phCapture(`live_broadcast_widget_${action}`, {
    provider: stream.provider,
    streamId: stream.id,
    providerStreamId: stream.providerStreamId,
    channelKey: stream.channelKey,
    channelId: stream.channelId,
    channelName: stream.channelName,
    viewerCount: stream.viewerCount,
    ...extra,
  });
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

const IDLE_DISCOVERY_POLL_MS = 5 * 60_000;

const REFOCUS_REFETCH_MIN_MS = 30_000;

export function LiveBroadcastProvider({ children }: { children: React.ReactNode }) {
  const [streams, setStreams] = useState<LiveBroadcastStream[]>([]);
  const [stage, setStage] = useState<LiveWidgetStage>("hidden");

  const prevLiveIdsRef = useRef<Set<string>>(new Set());
  const autoCollapseTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const pollTimerRef = useRef<ReturnType<typeof setInterval>>(undefined);
  const showDelayTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const isFirstFetchRef = useRef(true);
  const isLiveRef = useRef(false);
  const pollIntervalMsRef = useRef<number>(undefined);
  const lastFetchAtRef = useRef(0);
  const offlineCountRef = useRef<Map<string, number>>(new Map());
  const abortRef = useRef<AbortController>(undefined);

  const activeStream =
    streams.find((stream) => safeGet(liveBroadcastDismissedKey(stream)) === null) ?? null;
  const isLive = streams.length > 0;
  const streamsRef = useRef<LiveBroadcastStream[]>([]);
  const activeStreamRef = useRef<LiveBroadcastStream | null>(null);
  streamsRef.current = streams;
  activeStreamRef.current = activeStream;

  const fetchLiveData = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    lastFetchAtRef.current = Date.now();

    try {
      const res = await fetch("/api/live/broadcasts", { signal: controller.signal });
      if (!res.ok) return;

      const data: LiveBroadcastApiResponse = await res.json();
      if (controller.signal.aborted) return;

      const currentStreams = sortStreamsByPriority(data.streams ?? []);
      isLiveRef.current = currentStreams.length > 0;
      const desiredMs = isLiveRef.current
        ? LIVE_BROADCAST_CLIENT_POLL_INTERVAL_MS
        : IDLE_DISCOVERY_POLL_MS;
      if (pollIntervalMsRef.current !== desiredMs) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = undefined;
        pollIntervalMsRef.current = desiredMs;
        if (document.visibilityState === "visible") {
          pollTimerRef.current = setInterval(fetchLiveData, desiredMs);
        }
      }
      setStreams((prevStreams) =>
        JSON.stringify(prevStreams) === JSON.stringify(currentStreams) ? prevStreams : currentStreams,
      );

      const dismissalCache = new Map<string, boolean>();
      const isDismissed = (streamOrChannel: Parameters<typeof liveBroadcastDismissedKey>[0]) => {
        const key = liveBroadcastDismissedKey(streamOrChannel);
        const cached = dismissalCache.get(key);
        if (cached !== undefined) return cached;
        const dismissed = safeGet(key) !== null;
        dismissalCache.set(key, dismissed);
        return dismissed;
      };

      const currentIds = new Set(currentStreams.map((stream) => stream.id));
      const prevIds = prevLiveIdsRef.current;
      const newlyLive = currentStreams.filter((stream) => !prevIds.has(stream.id));
      prevLiveIdsRef.current = currentIds;

      if (newlyLive.length > 0 || (isFirstFetchRef.current && currentStreams.length > 0)) {
        const activatable = currentStreams.find((stream) => !isDismissed(stream));
        if (activatable) {
          const delay = isFirstFetchRef.current ? LIVE_WIDGET_SHOW_DELAY_MS : 0;
          clearTimeout(showDelayTimerRef.current);
          showDelayTimerRef.current = setTimeout(() => {
            setStage("expanded");
            captureLiveBroadcastEvent("shown", activatable);
            clearTimeout(autoCollapseTimerRef.current);
            autoCollapseTimerRef.current = setTimeout(() => {
              setStage((prev) => (prev === "expanded" ? "pill" : prev));
            }, LIVE_WIDGET_AUTO_COLLAPSE_MS);
          }, delay);
        }
      }

      const OFFLINE_POLLS_BEFORE_CLEAR = 3;
      const liveKeys = new Set(currentStreams.map((stream) => stream.channelKey));
      for (const channel of LIVE_BROADCAST_CHANNELS) {
        const key = liveBroadcastDismissedKey(channel);
        if (liveKeys.has(channel.channelKey)) {
          offlineCountRef.current.delete(key);
        } else if (safeGet(key) !== null) {
          const count = (offlineCountRef.current.get(key) ?? 0) + 1;
          if (count >= OFFLINE_POLLS_BEFORE_CLEAR) {
            safeRemove(key);
            offlineCountRef.current.delete(key);
            dismissalCache.set(key, false);
          } else {
            offlineCountRef.current.set(key, count);
          }
        }
      }

      const anyActivatable = currentStreams.some((stream) => !isDismissed(stream));
      if (currentStreams.length === 0 || !anyActivatable) {
        setStage("hidden");
        clearTimeout(autoCollapseTimerRef.current);
        clearTimeout(showDelayTimerRef.current);
      }

      isFirstFetchRef.current = false;
    } catch (err) {
      if (isAbortError(err)) return;
    }
  }, []);

  useEffect(() => {
    void fetchLiveData();

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        const intervalMs = pollIntervalMsRef.current;
        if (intervalMs !== undefined && pollTimerRef.current === undefined) {
          pollTimerRef.current = setInterval(fetchLiveData, intervalMs);
        }
        if (intervalMs === undefined || Date.now() - lastFetchAtRef.current >= REFOCUS_REFETCH_MIN_MS) {
          void fetchLiveData();
        }
      } else {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = undefined;
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      abortRef.current?.abort();
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = undefined;
      pollIntervalMsRef.current = undefined;
      clearTimeout(autoCollapseTimerRef.current);
      clearTimeout(showDelayTimerRef.current);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [fetchLiveData]);

  const expand = useCallback(() => {
    const stream = activeStreamRef.current;
    setStage("expanded");
    if (stream) {
      captureLiveBroadcastEvent("expanded", stream);
      clearTimeout(autoCollapseTimerRef.current);
      autoCollapseTimerRef.current = setTimeout(() => {
        setStage((prev) => (prev === "expanded" ? "pill" : prev));
      }, LIVE_WIDGET_AUTO_COLLAPSE_MS);
    }
  }, []);

  const dismiss = useCallback(() => {
    clearTimeout(autoCollapseTimerRef.current);
    clearTimeout(showDelayTimerRef.current);

    const stream = activeStreamRef.current;
    if (stream) {
      safeSet(liveBroadcastDismissedKey(stream), "1");
      captureLiveBroadcastEvent("dismissed", stream);
      const nextStream = streamsRef.current.find(
        (candidate) => candidate.id !== stream.id && safeGet(liveBroadcastDismissedKey(candidate)) === null,
      );
      setStage(nextStream ? "pill" : "hidden");
    } else {
      setStage("hidden");
    }
  }, []);

  const clickCta = useCallback((watchUrl: string, stream?: LiveBroadcastStream, surface?: "chat") => {
    const tracked = stream ?? activeStreamRef.current;
    if (tracked) {
      captureLiveBroadcastEvent("clicked", tracked, { watchUrl, surface });
    }
    window.open(watchUrl, "_blank", "noopener,noreferrer");
  }, []);

  const value = useMemo<LiveBroadcastContextValue>(
    () => ({ isLive, streams, activeStream, stage, expand, dismiss, clickCta }),
    [activeStream, clickCta, dismiss, expand, isLive, stage, streams],
  );

  return <LiveBroadcastContext.Provider value={value}>{children}</LiveBroadcastContext.Provider>;
}
